// src/services/AuthService.js
// Versión modificada para usar localStorage en lugar de sessionStorage
import { jwtDecode } from 'jwt-decode';
import { AUTH } from '../utils/URLS';

class AuthService {
  // En memoria: solo disponible durante la sesión activa del navegador
  static #accessToken = null;
  static #user = null;
  
  /**
   * Inicializa el servicio de autenticación al cargar la aplicación
   * Intenta recuperar el token de la sesión
   */
  static initialize() {
    // ✅ CAMBIO: Usar localStorage en lugar de sessionStorage
    this.#accessToken = localStorage.getItem('access_token');
    
    // Si tenemos token, extraer información del usuario
    if (this.#accessToken) {
      try {
        const decoded = jwtDecode(this.#accessToken);
        this.#user = decoded.username || decoded.sub;
        
        // Verificar si el token ha expirado
        const currentTime = Date.now() / 1000;
        if (decoded.exp && decoded.exp < currentTime) {
          //console.log('Token expirado, redirigiendo a login...');
          this.logout();
        }
      } catch (error) {
        console.error('Error al decodificar token:', error);
        this.logout();
      }
    }
  }
  
  /**
   * Realiza la autenticación del usuario
   * @param {string} username - Nombre de usuario
   * @param {string} password - Contraseña
   * @returns {Promise<boolean>} - True si la autenticación fue exitosa
   */
  static async login(username, password) {
    try {
      const response = await fetch(AUTH.LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      
      if (!response.ok) {
        return false;
      }
      
      const data = await response.json();
      
      // Extraer token
      this.#accessToken = data.access_token;
      
      // ✅ CAMBIO: Almacenar en localStorage en lugar de sessionStorage
      localStorage.setItem('access_token', this.#accessToken);
      
      // Extraer información del usuario del token
      try {
        const decoded = jwtDecode(this.#accessToken);
        this.#user = decoded.username || decoded.sub;
        // ✅ CAMBIO: Usar localStorage para el usuario también
        localStorage.setItem('user', this.#user);
      } catch (error) {
        console.warn('No se pudo decodificar el token. Guardando username directamente.');
        this.#user = username;
        // ✅ CAMBIO: Usar localStorage para el usuario también
        localStorage.setItem('user', username);
      }
      
      return true;
    } catch (error) {
      console.error('Error durante el login:', error);
      return false;
    }
  }
  
  /**
   * Cierra la sesión del usuario
   */
  static logout() {
    this.#accessToken = null;
    this.#user = null;
    
    // ✅ CAMBIO: Limpiar localStorage en lugar de sessionStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
  }
  
  /**
   * Obtiene el token de acceso actual
   * @returns {string|null} - Token de acceso o null si no hay sesión
   */
  static getAccessToken() {
    return this.#accessToken;
  }
  
  /**
   * Obtiene el nombre de usuario actual
   * @returns {string|null} - Nombre de usuario o null si no hay sesión
   */
  static getUser() {
    return this.#user;
  }
  
  /**
   * Verifica si el usuario está autenticado
   * @returns {boolean} - True si el usuario está autenticado
   */
  static isAuthenticated() {
    return !!this.#accessToken;
  }
  
  /**
   * Verifica si la sesión ha expirado
   * @returns {boolean} - True si la sesión ha expirado
   */
  static isSessionExpired() {
    if (!this.#accessToken) return true;
    
    try {
      const decoded = jwtDecode(this.#accessToken);
      // Si el token no tiene exp, asumimos que no expira
      if (!decoded.exp) return false;
      
      const currentTime = Date.now() / 1000;
      return decoded.exp < currentTime;
    } catch (error) {
      console.error('Error al verificar expiración del token:', error);
      return true;
    }
  }

  /**
   * Cambia la contraseña del usuario autenticado.
   * @param {string} currentPassword
   * @param {string} newPassword
   * @returns {Promise<{success: boolean, message: string}>}
   */
  static async changePassword(currentPassword, newPassword) {
    const response = await fetch(AUTH.CHANGE_PASSWORD, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.#accessToken}`,
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = Array.isArray(data.message)
        ? data.message.join(', ')
        : data.message || 'No se pudo cambiar la contraseña';
      return { success: false, message };
    }
    return { success: true, message: data.message || 'Contraseña actualizada' };
  }
}

// Inicializar al importar
AuthService.initialize();

export default AuthService;